// Явный импорт вместо авто-импорта Nuxt — этот файл сам является composable'ом и
// дёргает другой composable (useAuth) изнутри; после долгой серии HMR-правок за
// сессию авто-импорт для таких "вложенных" вызовов иногда переставал резолвиться
// (см. ReferenceError: useAuth is not defined, поймано error-tracking.client.ts),
// роняя весь layouts/default.vue. Явный import не зависит от этого сканирования.
import { useAuth } from './useAuth'

export interface MainNavItem {
  name: string
  href: string
  icon: string
  featureKey?: string
  adminOnly?: boolean
  superAdminOnly?: boolean
  children?: { name: string, href: string, icon?: string }[]
}

// featureKey: гейтится флагом команды (team_feature_flags, управляется суперадмином).
// adminOnly: гейтится ролью (админ команды/суперадмин), не тумблером команды.
export const allMainNavigation: MainNavItem[] = [
  { name: 'Обзор', href: '/', icon: 'lucide:layout-dashboard' },
  { name: 'Почта', href: '/mail', icon: 'lucide:mail', featureKey: 'page:mail' },
  { name: 'Календарь', href: '/calendar', icon: 'lucide:calendar-days' },
  { name: 'Задачи', href: '/tasks', icon: 'lucide:check-square' },
  { name: 'Тендеры', href: '/tenders', icon: 'lucide:file-text' },
  { name: 'Товары', href: '/products/catalog', icon: 'lucide:package', children: [
    { name: 'Каталог', href: '/products/catalog' },
    { name: 'Мои товары', href: '/products/my' },
    // Справочник КТРУ: характеристики, значения и как они записаны у товаров (pages/ktru_char.vue)
    { name: 'Характеристики КТРУ', href: '/ktru_char' },
    // Прайсы поставщиков -> категории ОКПД2/КТРУ и характеристики КТРУ (pages/price_mapping.vue)
    { name: 'Сопоставление прайсов', href: '/price_mapping' },
    // Поля прайсов DSSL / Absolut Trade -> справочник КТРУ по правилам импорта, ручная привязка (pages/vendor_fields.vue)
    { name: 'Характеристики поставщиков', href: '/vendor_fields' },
    // «Остатки» убраны из меню 2026-10-25 — остаток правится в «Моих товарах»
  ]},
  // 2026-10-25: «Поставщики» → «Контакты» (вкладки Производители / Поставщики / Заказчики + свои группы)
  { name: 'Контакты', href: '/contacts', icon: 'lucide:contact' },
  { name: 'КП', href: '/commercial_proposal', icon: 'lucide:file-signature', children: [
    { name: 'Создать', href: '/commercial_proposal' },
    { name: 'Запросы', href: '/commercial_proposal/requests' },
    { name: 'Настройки', href: '/commercial_proposal/settings' },
  ]},
  { name: 'Аналитика', href: '/reports', icon: 'lucide:bar-chart-3' },
  // Бухгалтерия: выручка выигранных − НДС − налог, оклад + % сотрудникам (pages/accounting.vue)
  { name: 'Бухгалтерия', href: '/accounting', icon: 'lucide:calculator' },
  { name: 'Пользователи', href: '/users', icon: 'lucide:users', adminOnly: true },
  { name: 'Маппинг', href: '/mapping', icon: 'lucide:git-merge', superAdminOnly: true },
  { name: 'Команды', href: '/admin/teams', icon: 'lucide:shield', superAdminOnly: true, children: [
    { name: 'Команды', href: '/admin/teams' },
    { name: 'Лимиты AI', href: '/admin/limits' },
    { name: 'Нагрузка сервера', href: '/admin/server' },
  ]},
  // Лимиты AI команды: счётчик тендеров в бейдже (Sidebar.vue), страница — кто какой тендер обрабатывал
  { name: 'Лимиты', href: '/limits', icon: 'lucide:gauge' },
]

export function useMainNavigation() {
  const { isAdmin, isSuperAdmin, hasFeature, canViewCatalog } = useAuth()
  const hidden = sidebarHidden()

  const navigation = computed(() =>
    allMainNavigation.filter(item => {
      if (hidden.has(item.href)) return false
      if (item.superAdminOnly && !isSuperAdmin.value) return false
      if (item.adminOnly && !isAdmin.value) return false
      if (item.featureKey && !hasFeature(item.featureKey)) return false
      return true
    }).map(item => {
      // Без доступа к общему каталогу «Товары» — только «Мои товары»
      if (canViewCatalog.value || !item.children?.some(c => isCatalogPath(c.href))) return item
      const children = item.children.filter(c => !isCatalogPath(c.href))
      return { ...item, href: children[0]?.href || item.href, children }
    })
  )

  return { navigation }
}

// Пункты сайдбара, скрытые на конкретной установке: NUXT_PUBLIC_SIDEBAR_HIDE — пути через
// запятую (`/mapping,/limits`), `ai` — кнопка «AI-ассистент» в футере. На crm.zakupkihub.ru
// задано в docker-compose (2026-10-18), на 192.168.1.88 пусто. Страницы по прямой ссылке работают.
export function sidebarHidden(): Set<string> {
  const raw = String(useRuntimeConfig().public.sidebarHide || '')
  return new Set(raw.split(',').map(s => s.trim()).filter(Boolean))
}

// Страницы общего каталога товаров (закрыты без catalog_access — middleware/auth.global.ts)
export function isCatalogPath(path: string): boolean {
  return /^\/(products\/catalog|catalog2?|pc-builder|ktru_char|price_mapping|vendor_fields)(\/|$)/.test(path)
}
